<template>
  <div class="cs-preview">
      <div
        v-if="preview && !loading && !failure"
        class="cs-preview__verified"
        role="status"
        aria-live="polite">
        <v-icon size="20" color="success">mdi-check-circle-outline</v-icon>
        <div>
          <div class="cs-preview__verified-title">Preview verified</div>
          <div class="cs-preview__muted">
            This exact local definition ran successfully. Review the sample before finishing.
          </div>
        </div>
      </div>

      <!-- Executive Metrics & Diagnostics Bar -->
      <div v-if="preview && !loading && !failure" class="cs-preview-metrics">
        <div class="cs-metric-card">
          <div class="cs-metric-card__icon cs-metric-card__icon--primary">
            <v-icon size="18" color="primary">mdi-table-row</v-icon>
          </div>
          <div>
            <div class="cs-metric-card__val">{{ rowCountLabel }}</div>
            <div class="cs-metric-card__lbl">Sample Cap ({{ preview.rowLimit || 50 }} Max)</div>
          </div>
        </div>

        <div class="cs-metric-card">
          <div class="cs-metric-card__icon cs-metric-card__icon--mint">
            <v-icon size="18" color="secondary">mdi-timer-outline</v-icon>
          </div>
          <div>
            <div class="cs-metric-card__val">{{ preview.totalMilliseconds || (preview.compileMilliseconds + preview.executeMilliseconds) }} ms</div>
            <div class="cs-metric-card__lbl">{{ timingDetailLabel }}</div>
          </div>
        </div>

        <div class="cs-metric-card">
          <div class="cs-metric-card__icon cs-metric-card__icon--blue">
            <v-icon size="18" color="blue">mdi-view-column-outline</v-icon>
          </div>
          <div>
            <div class="cs-metric-card__val">{{ preview.columns.length }} Projected</div>
            <div class="cs-metric-card__lbl">Columns in View</div>
          </div>
        </div>

        <div class="cs-metric-card">
          <div class="cs-metric-card__icon cs-metric-card__icon--tag">
            <v-icon size="18" color="primary">mdi-cube-scan</v-icon>
          </div>
          <div>
            <div class="cs-metric-card__val">{{ tagStatusLabel }}</div>
            <div class="cs-metric-card__lbl">{{ tagSubtitleLabel }}</div>
          </div>
        </div>
      </div>

      <!-- Fan-Out Warning Banner -->
      <div v-if="fanOutNote && !loading && !failure" class="cs-preview__grain">
        <v-icon size="20" color="warning" class="cs-preview__grain-icon">mdi-call-split</v-icon>
        <div>
          <div class="cs-preview__grain-title">{{ copy.GrainFanOutTitle }}</div>
          <div class="cs-preview__muted">{{ fanOutNote }}</div>
        </div>
      </div>

      <!-- Grid Control Bar -->
      <div v-if="preview && !loading && !failure" class="cs-grid-toolbar">
        <div class="cs-grid-toolbar__search">
          <v-text-field
            v-model="tableSearch"
            aria-label="Search preview rows"
            placeholder="Search rows in preview table..."
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            class="cs-preview-search" />
        </div>

        <div class="cs-grid-toolbar__actions">
          <v-menu v-model="moreMenuOpen" offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                outlined
                small
                aria-label="More preview options"
                aria-haspopup="menu">
                <v-icon left size="16">mdi-dots-horizontal</v-icon>
                More
              </v-btn>
            </template>

            <v-list dense class="cs-preview__more-menu">
              <v-subheader>Table density</v-subheader>
              <v-list-item
                v-for="density in densityOptions"
                :key="density.value"
                :aria-pressed="String(tableDensity === density.value)"
                @click="setTableDensity(density.value)">
                <v-list-item-icon>
                  <v-icon size="17" :color="tableDensity === density.value ? 'primary' : undefined">
                    {{ tableDensity === density.value ? 'mdi-check' : 'mdi-circle-small' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ density.text }}</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="openIllustrativeSql">
                <v-list-item-icon><v-icon size="17">mdi-code-braces</v-icon></v-list-item-icon>
                <v-list-item-title>View illustrative SQL</v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="!preview.rows.length" @click="exportCsv">
                <v-list-item-icon><v-icon size="17">mdi-download</v-icon></v-list-item-icon>
                <v-list-item-title>Export sample CSV</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn
            depressed
            small
            color="primary"
            :disabled="loading"
            :loading="loading"
            @click="runPreview">
            <v-icon left size="16">mdi-refresh</v-icon>
            {{ copy.PreviewRerun }}
          </v-btn>
        </div>
      </div>

      <!-- State 2: Loading State -->
      <div v-if="loading" class="cs-preview__state" role="status" aria-live="polite">
        <v-progress-circular indeterminate color="primary" size="36" width="3" />
        <div class="cs-preview__state-title" style="margin-top: 8px;">{{ copy.PreviewRunning }}</div>
        <div class="cs-preview__muted">Compiling the local definition and fetching a row sample...</div>
      </div>

      <!-- State 3: Execution Failure -->
      <div v-else-if="failure" class="cs-preview__state" role="alert">
        <v-icon size="36" color="error">mdi-alert-circle-outline</v-icon>
        <div class="cs-preview__state-title">{{ copy.PreviewFailedTitle }}</div>
        <div v-if="failure.issues.length" class="cs-preview__issues">
          <div v-for="(issue, i) in failure.issues" :key="i" class="cs-preview__issue">
            <span v-if="issue.step" class="cs-preview__issue-step">{{ issue.step }}</span>
            <span>{{ issue.message }}</span>
          </div>
        </div>
        <div v-else class="cs-preview__muted">{{ failure.message }}</div>
        <v-btn depressed small color="primary" style="margin-top: 12px;" @click="runPreview">
          <v-icon left size="16">mdi-refresh</v-icon>
          Retry Preview
        </v-btn>
      </div>

      <!-- State 4: Empty Results -->
      <div v-else-if="preview && !preview.rows.length" class="cs-preview__state" role="status">
        <v-icon size="36" color="primary">mdi-table-off</v-icon>
        <div class="cs-preview__state-title">{{ copy.PreviewEmptyTitle }}</div>
        <div class="cs-preview__muted">{{ copy.PreviewEmptyMessage }}</div>
        <v-btn outlined small color="primary" style="margin-top: 12px;" @click="runPreview">
          <v-icon left size="16">mdi-refresh</v-icon>
          Re-run Query
        </v-btn>
      </div>

      <!-- State 5: Rich Interactive Data Grid (DEFAULT SPACIOUS DENSITY) -->
      <div v-else-if="preview" class="cs-preview__table-container">
        <table class="cs-preview-table" :class="tableDensityClass">
          <caption class="cs-preview__sr-only">Preview sample rows</caption>
          <thead>
            <tr>
              <th
                v-for="column in preview.columns"
                :key="column"
                :aria-sort="ariaSort(column)">
                <button
                  type="button"
                  class="cs-th-inner"
                  :aria-label="sortAriaLabel(column)"
                  @click="sortBy(column)">
                  <span>{{ column }}</span>
                  <v-icon size="14" class="cs-th-sort" :class="{ 'cs-th-sort--active': sortColumn === column }">
                    {{ sortIcon(column) }}
                  </v-icon>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, rowIndex) in filteredSortedRows"
              :key="rowIndex"
              :class="{ 'cs-row-fanout': isFanoutRow(row) }">
              <td v-for="column in preview.columns" :key="column">
                <!-- Null Cell -->
                <span v-if="isNull(row[column])" class="cs-cell-null">
                  {{ copy.PreviewNullCell }}
                </span>

                <!-- Boolean Cell -->
                <span v-else-if="typeof row[column] === 'boolean'" class="cs-cell-bool" :class="row[column] ? 'cs-cell-bool--true' : 'cs-cell-bool--false'">
                  {{ row[column] ? 'Yes' : 'No' }}
                </span>

                <!-- ID or Tag Column -->
                <span v-else-if="isIdColumn(column)" class="cs-cell-id">
                  {{ row[column] }}
                </span>

                <!-- Number Column -->
                <span v-else-if="typeof row[column] === 'number'" class="cs-cell-number">
                  {{ row[column] }}
                </span>

                <!-- Standard Text -->
                <span v-else class="cs-cell-text">
                  {{ row[column] }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    <!-- SQL View Definition Modal -->
    <v-dialog v-model="sqlModalOpen" max-width="680" persistent>
      <v-card>
        <v-card-title class="cs-sql-modal__head">
          <div style="display: flex; align-items: center; gap: 8px;">
            <v-icon size="20" color="primary">mdi-code-braces</v-icon>
            <span>Illustrative SQL shape (not executed)</span>
          </div>
          <v-btn
            icon
            small
            aria-label="Close illustrative SQL"
            title="Close illustrative SQL"
            @click="sqlModalOpen = false">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="padding-top: 16px;">
          <p class="cs-columns__muted" style="margin-bottom: 10px;">
            Generated locally for orientation only. This is not the SQL executed by the server
            and may differ from the compiled source.
          </p>
          <pre class="cs-sql-code">{{ illustrativeSql }}</pre>
        </v-card-text>

        <v-card-actions class="cs-sql-modal__actions">
          <v-spacer />
          <v-btn text small color="primary" @click="copySql">
            <v-icon left size="16">mdi-content-copy</v-icon>
            Copy SQL
          </v-btn>
          <v-btn depressed small color="primary" @click="sqlModalOpen = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="2500" bottom right role="status" aria-live="polite">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
  import { log, error } from '@/utils/debugLogger';
  import compositeSourceService from '../../service/compositeSourceService';
  import { RootNodeId } from '../../model/definitionVocabulary';
  import { PreviewPublishCopy, validationIssues, describeFailure } from './previewPublishCopy';

  const CompilerHiddenColumns = new Set([
    '__rowkey',
    '__registerrowid',
    '__targetid',
    '__contexttagid',
  ]);

  function isCompilerHiddenColumn(columnName) {
    return CompilerHiddenColumns.has(String(columnName || '').toLowerCase());
  }

  /**
   * Step 4 — run the latest local definition and show an interactive, spacious preview.
   *
   * Enhanced with:
   * 1. Default Spacious density table with high-contrast type formatters.
   * 2. In-table row filtering and ASC/DESC column sorting.
   * 3. Executive diagnostics metrics (RowCount, Compile/Execution timing, Columns, 3D Binding).
   * 4. View SQL modal and CSV sample export.
   */
  export default {
    name: 'StepPreview',

    props: {
      /** definitionModel for this authoring session. */
      model: { type: Object, required: true },
      /** Authoring graph for the local root, or null while it is being resolved. */
      graph: { type: Object, default: null },
      /** Selected workspace target: {kind, id, name}. */
      target: { type: Object, required: true },
    },

    data() {
      return {
        copy: PreviewPublishCopy,
        preview: null,
        failure: null,
        loading: false,
        requestToken: 0,
        // UI Controls
        tableSearch: '',
        tableDensity: 'spacious', // 'compact' | 'normal' | 'spacious' (DEFAULT SPACIOUS)
        sortColumn: null,
        sortDesc: false,
        moreMenuOpen: false,
        sqlModalOpen: false,
        snackbar: false,
        snackbarText: '',
        densityOptions: [
          { text: 'Compact', value: 'compact' },
          { text: 'Normal', value: 'normal' },
          { text: 'Spacious', value: 'spacious' },
        ],
      };
    },

    computed: {
      rowCountLabel() {
        if (!this.preview) return '';
        const { rowCount, rowLimit } = this.preview;
        const rows = `${rowCount} ${rowCount === 1 ? 'Row' : 'Rows'}`;
        return rowCount >= rowLimit
          ? `${rows} (Capped)`
          : rows;
      },

      timingDetailLabel() {
        if (!this.preview) return '';
        const { compileMilliseconds, executeMilliseconds } = this.preview;
        return `Compile: ${compileMilliseconds}ms \u2022 Exec: ${executeMilliseconds}ms`;
      },

      tagStatusLabel() {
        const sourceId = this.model.state.tagColumnSource;
        if (!sourceId) return 'No 3D Tag';
        const node = this.model.nodes().find((n) => n.id === sourceId);
        return node ? `${node.label}` : '3D Bound';
      },

      tagSubtitleLabel() {
        const sourceId = this.model.state.tagColumnSource;
        if (!sourceId) return 'TagId unassigned';
        return 'Supplies 3D TagId';
      },

      fanOutNote() {
        const grainNodeId = this.model.grain();
        if (!grainNodeId || grainNodeId === RootNodeId) return null;

        const node = this.model.nodes().find((candidate) => candidate.id === grainNodeId);
        const label = (node && node.label) || 'A relation';
        return `${label} fans out, so a record appears once per matching row rather than once in total.`;
      },

      tableDensityClass() {
        switch (this.tableDensity) {
          case 'compact': return 'cs-preview-table--compact';
          case 'normal': return 'cs-preview-table--normal';
          default: return 'cs-preview-table--spacious'; // Default Spacious
        }
      },

      filteredSortedRows() {
        if (!this.preview || !this.preview.rows) return [];
        let rows = [...this.preview.rows];

        // 1. Text Search Filter
        const term = (this.tableSearch || '').trim().toLowerCase();
        if (term) {
          rows = rows.filter((row) => Object.values(row).some((val) => String(val || '').toLowerCase().includes(term)));
        }

        // 2. Sorting
        if (this.sortColumn) {
          const col = this.sortColumn;
          const desc = this.sortDesc;
          rows.sort((a, b) => {
            const valA = a[col];
            const valB = b[col];
            if (valA === valB) return 0;
            if (valA === null || valA === undefined) return 1;
            if (valB === null || valB === undefined) return -1;
            if (typeof valA === 'number' && typeof valB === 'number') {
              return desc ? valB - valA : valA - valB;
            }
            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();
            return desc ? strB.localeCompare(strA) : strA.localeCompare(strB);
          });
        }

        return rows;
      },

      illustrativeSql() {
        if (!this.preview) return '-- ILLUSTRATIVE ONLY — preview results are not available';
        const cols = this.preview.columns.map((c) => `    [t].[${c}]`).join(',\n');
        const sourceName = (this.target && this.target.name ? this.target.name : 'Composite')
          .replace(/[^a-zA-Z0-9_]/g, '_');
        return `-- ILLUSTRATIVE ONLY — NOT EXECUTED BY THE SERVER\nSELECT\n${cols}\nFROM [dbo].[vw_cs_${sourceName}_v1] [t]\nWHERE [t].[FacilityId] = '<FACILITY_ID>';`;
      },
    },

    mounted() {
      this.runPreview();
    },

    beforeDestroy() {
      this.requestToken += 1;
    },

    methods: {
      async runPreview() {
        if (!this.model || !this.target) return;

        this.requestToken += 1;
        const token = this.requestToken;
        this.loading = true;
        this.failure = null;
        this.preview = null;
        this.$emit('preview-invalidated');

        let definitionJson;
        let candidateSnapshot;
        try {
          definitionJson = this.model.toDefinitionJson();
          candidateSnapshot = {
            name: this.model.state.name || '',
            json: definitionJson,
          };
        } catch (err) {
          this.loading = false;
          this.failure = {
            issues: [],
            message: err && err.message ? err.message : this.copy.PreviewFailedFallback,
          };
          return;
        }

        log('[StepPreview] preview requested', {
          targetId: this.target.id,
        });

        try {
          const res = await compositeSourceService.preview(definitionJson, this.target.id);
          if (token !== this.requestToken) return;

          this.preview = this.normalize(res && res.data);
          this.$emit('preview-verified', candidateSnapshot);
          log('[StepPreview] preview returned', {
            columns: this.preview.columns.length,
            rowCount: this.preview.rowCount,
            rowLimit: this.preview.rowLimit,
            grain: this.preview.grain,
            totalMilliseconds: this.preview.totalMilliseconds,
          });
        } catch (err) {
          if (token !== this.requestToken) return;

          error('[StepPreview] preview failed', { targetId: this.target.id, err });
          this.preview = null;
          this.failure = {
            issues: validationIssues(err),
            message: describeFailure(err, this.copy.PreviewFailedFallback),
          };
        } finally {
          if (token === this.requestToken) this.loading = false;
        }
      },

      normalize(data) {
        const columns = ((data && data.columns) || [])
          .filter(column => !isCompilerHiddenColumn(column));
        const rows = ((data && data.rows) || []).map(row => Object.keys(row || {}).reduce((visible, key) => {
          if (!isCompilerHiddenColumn(key)) visible[key] = row[key];
          return visible;
        }, {}));
        return {
          columns,
          rows,
          rowCount: (data && data.rowCount) || 0,
          rowLimit: (data && data.rowLimit) || 0,
          grain: (data && data.grain) || null,
          compileMilliseconds: (data && data.compileMilliseconds) || 0,
          executeMilliseconds: (data && data.executeMilliseconds) || 0,
          totalMilliseconds: (data && data.totalMilliseconds) || 0,
        };
      },

      isNull(value) {
        return value === null || value === undefined;
      },

      isIdColumn(columnName) {
        if (!columnName) return false;
        const name = columnName.toLowerCase();
        return name.endsWith('id') || name.endsWith('guid') || name.includes('tag');
      },

      isFanoutRow(row) {
        return this.fanOutNote && row && row.__isFanout;
      },

      sortBy(column) {
        if (this.sortColumn === column) {
          if (this.sortDesc) {
            this.sortColumn = null;
            this.sortDesc = false;
          } else {
            this.sortDesc = true;
          }
        } else {
          this.sortColumn = column;
          this.sortDesc = false;
        }
      },

      sortIcon(column) {
        if (this.sortColumn !== column) return 'mdi-unfold-more-horizontal';
        return this.sortDesc ? 'mdi-chevron-down' : 'mdi-chevron-up';
      },

      ariaSort(column) {
        if (this.sortColumn !== column) return 'none';
        return this.sortDesc ? 'descending' : 'ascending';
      },

      sortAriaLabel(column) {
        if (this.sortColumn !== column) return `Sort ${column} ascending`;
        if (!this.sortDesc) return `Sort ${column} descending`;
        return `Clear sorting for ${column}`;
      },

      setTableDensity(density) {
        this.tableDensity = density;
      },

      openIllustrativeSql() {
        this.moreMenuOpen = false;
        this.sqlModalOpen = true;
      },

      copySql() {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(this.illustrativeSql)
          .then(() => {
            this.snackbarText = 'Illustrative SQL copied to clipboard';
            this.snackbar = true;
          })
          .catch((err) => {
            error('[StepPreview] copy SQL failed', err);
          });
      },

      exportCsv() {
        if (!this.preview || !this.preview.rows.length) return;

        const cols = this.preview.columns;
        const headerLine = cols.map((c) => `"${c.replace(/"/g, '""')}"`).join(',');
        const rowLines = this.filteredSortedRows.map((row) => (
          cols.map((col) => {
            const val = row[col];
            if (this.isNull(val)) return '""';
            return `"${String(val).replace(/"/g, '""')}"`;
          }).join(',')
        ));

        const csvContent = [headerLine, ...rowLines].join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'composite_source_preview.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.snackbarText = 'Sample CSV downloaded';
        this.snackbar = true;
      },
    },
  };
</script>

<style scoped>
  .cs-preview {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .cs-columns__muted {
    font-size: 0.8125rem;
    color: var(--v-text-base);
  }

  .cs-preview__muted {
    color: var(--v-text-base);
    font-size: 0.8125rem;
  }

  .cs-preview__verified {
    display: flex;
    align-items: flex-start;
    gap: var(--ds-space-sm);
    padding: 10px 14px;
    margin-bottom: var(--ds-space-sm);
    border: 1px solid var(--v-success-base);
    border-radius: var(--ds-radius-sm);
    background: var(--ds-surface-active);
  }

  .cs-preview__verified-title {
    font-family: var(--ds-font-family-heading);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v-text-base);
  }

  /* ---------------- Diagnostics Metrics Bar ---------------- */
  .cs-preview-metrics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
    margin-bottom: 12px;
    flex-shrink: 0;
  }

  .cs-metric-card {
    background: var(--ds-surface-elevated);
    border: 1px solid var(--ds-border-default);
    border-radius: var(--ds-radius-sm);
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    box-shadow: var(--ds-shadow-1);
  }

  .cs-metric-card__icon {
    width: 34px;
    height: 34px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .cs-metric-card__icon--primary,
  .cs-metric-card__icon--mint,
  .cs-metric-card__icon--blue,
  .cs-metric-card__icon--tag { background: var(--ds-surface-active); }

  .cs-metric-card__val {
    font-family: var(--ds-font-family-heading);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v-text-base);
    line-height: 1.2;
  }

  .cs-metric-card__lbl {
    font-size: 0.6875rem;
    color: var(--v-text-base);
    margin-top: 1px;
  }

  /* Fan-out Grain Warning */
  .cs-preview__grain {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    border: 1px solid var(--v-golden-base);
    background: var(--ds-surface-hover);
    border-radius: 4px;
    padding: 10px 14px;
    margin-bottom: 12px;
    flex-shrink: 0;
  }

  .cs-preview__grain-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--v-text-base);
  }

  /* Grid Control Bar */
  .cs-grid-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    background: var(--v-bg2-base);
    padding: 8px 12px;
    border: 1px solid var(--ds-border-default);
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    flex-shrink: 0;
  }

  .cs-grid-toolbar__search {
    flex: 1;
    max-width: 320px;
  }

  .cs-preview-search ::v-deep .v-input__control {
    min-height: 32px;
  }

  .cs-preview-search ::v-deep input {
    font-size: 0.75rem;
  }

  .cs-grid-toolbar__actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .cs-preview__more-menu {
    min-width: 240px;
  }

  /* ---------------- Interactive Data Table ---------------- */
  .cs-preview__table-container {
    border: 1px solid var(--ds-border-default);
    border-radius: 0 0 4px 4px;
    overflow: auto;
    background: var(--ds-surface-elevated);
    flex: 1;
    min-height: 0;
    box-shadow: var(--ds-shadow-1);
  }

  .cs-preview-table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
  }

  .cs-preview-table thead {
    position: sticky;
    top: 0;
    background: var(--v-bg7-base);
    z-index: 10;
    border-bottom: 2px solid var(--ds-border-default);
  }

  .cs-preview-table th {
    font-family: var(--ds-font-family-heading);
    font-weight: 600;
    color: var(--v-text-base);
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s ease;
  }

  .cs-preview-table th:hover {
    background: var(--ds-surface-hover);
  }

  .cs-th-inner {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }

  .cs-th-inner:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: 2px;
  }

  .cs-th-sort {
    opacity: 0.4;
  }

  .cs-th-sort--active {
    opacity: 1;
    color: var(--v-primary-base);
  }

  .cs-preview-table td {
    border-bottom: 1px solid var(--ds-border-subtle);
    white-space: nowrap;
    color: var(--v-text-base);
  }

  .cs-preview-table tbody tr:hover {
    background: var(--ds-surface-hover);
  }

  /* DEFAULT SPACIOUS DENSITY STYLES */
  .cs-preview-table--spacious th {
    padding: 13px 18px;
    font-size: 0.8125rem;
  }

  .cs-preview-table--spacious td {
    padding: 13px 18px;
    font-size: 0.8125rem;
  }

  /* Compact & Normal density overrides */
  .cs-preview-table--compact th {
    padding: 6px 10px;
    font-size: 0.6875rem;
  }

  .cs-preview-table--compact td {
    padding: 5px 10px;
    font-size: 0.6875rem;
  }

  .cs-preview-table--normal th {
    padding: 9px 14px;
    font-size: 0.75rem;
  }

  .cs-preview-table--normal td {
    padding: 8px 14px;
    font-size: 0.75rem;
  }

  /* Cell Type Renderers */
  .cs-cell-null {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 2px;
    font-size: 0.6875rem;
    color: var(--v-text-base);
    background: var(--ds-surface-hover);
    font-family: var(--ds-font-family-mono, monospace);
  }

  .cs-cell-id {
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.75rem;
    background: var(--v-bg7-base);
    padding: 2px 7px;
    border-radius: 2px;
    border: 1px solid var(--ds-border-subtle);
    font-weight: 500;
  }

  .cs-cell-bool {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 0.6875rem;
    font-weight: 600;
  }

  .cs-cell-bool--true {
    background: var(--ds-surface-active);
    color: var(--v-text-base);
    border: 1px solid var(--v-secondary-base);
  }

  .cs-cell-bool--false {
    background: var(--ds-surface-hover);
    color: var(--v-text-base);
  }

  .cs-cell-number {
    font-family: var(--ds-font-family-mono, monospace);
  }

  /* State Blocks */
  .cs-preview__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    padding: 64px 24px;
  }

  .cs-preview__state-title {
    font-family: var(--ds-font-family-heading);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v-text-base);
  }

  .cs-preview__issues {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 620px;
    text-align: left;
    margin-top: 8px;
  }

  .cs-preview__issue {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 0.8125rem;
    color: var(--v-text-base);
  }

  .cs-preview__issue-step {
    flex-shrink: 0;
    font-weight: 600;
    color: var(--v-error-base);
  }

  /* SQL Modal */
  .cs-sql-modal__head {
    padding: 14px 20px;
    border-bottom: 1px solid var(--ds-border-default);
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.9375rem;
    font-weight: 600;
  }

  .cs-sql-code {
    background: var(--v-bg2-base);
    color: var(--v-text-base);
    border: 1px solid var(--ds-border-default);
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.75rem;
    line-height: 1.6;
    padding: 14px;
    border-radius: 4px;
    overflow-x: auto;
  }

  .cs-sql-modal__actions {
    padding: 12px 24px;
    background: var(--v-bg2-base);
  }

  .cs-preview__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
